import { ref, onMounted, onUnmounted } from 'vue';
import { fetchAuthSession } from 'aws-amplify/auth'; // v6 import
import { forceTokenRefresh } from '../utils/auth-helpers';

export type ProvisioningStatus = 'LOADING' | 'PROVISIONING' | 'READY' | 'ERROR';
type ClaimPayload = Record<string, unknown> | undefined

const ONBOARDING_STATUS_KEY = 'custom:onboarding_status'
const CONTEXT_ORG_ID_KEY = 'custom:context_org_id'
const CONTEXT_TENANT_ID_KEY = 'custom:context_tenant_id'
const PENDING_PROVISIONING_STATUS = 'PENDING_PROVISIONING'

const resolveProvisioningStatus = (claims: ClaimPayload): ProvisioningStatus => {
  if (!claims) {
    return 'ERROR'
  }

  const orgId = claims[CONTEXT_ORG_ID_KEY]
  const tenantId = claims[CONTEXT_TENANT_ID_KEY]
  const hasOrgId = typeof orgId === 'string' && orgId.trim().length > 0
  const hasTenantId = typeof tenantId === 'string' && tenantId.trim().length > 0

  if (hasOrgId && hasTenantId) {
    return 'READY'
  }

  if (claims[ONBOARDING_STATUS_KEY] === PENDING_PROVISIONING_STATUS) {
    return 'PROVISIONING'
  }

  return 'ERROR'
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

interface WaitForProvisioningReadyOptions {
  maxAttempts?: number
  pollIntervalMs?: number
}

export async function waitForProvisioningReady(options: WaitForProvisioningReadyOptions = {}): Promise<void> {
  const maxAttempts = options.maxAttempts ?? 10
  const pollIntervalMs = options.pollIntervalMs ?? 3000

  const session = await fetchAuthSession()
  const initialClaims = session.tokens?.idToken?.payload as ClaimPayload
  const initialStatus = resolveProvisioningStatus(initialClaims)

  if (initialStatus === 'READY') {
    return
  }

  if (initialStatus !== 'PROVISIONING') {
    throw new Error('We could not confirm your organization setup. Missing org or tenant access context.')
  }

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const refreshedSession = await forceTokenRefresh()
    const refreshedClaims = refreshedSession.tokens?.idToken?.payload as ClaimPayload
    const refreshedStatus = resolveProvisioningStatus(refreshedClaims)

    if (refreshedStatus === 'READY') {
      return
    }

    if (refreshedStatus !== 'PROVISIONING') {
      throw new Error('We could not confirm your organization setup. Missing org or tenant access context.')
    }

    if (attempt < maxAttempts) {
      await wait(pollIntervalMs)
    }
  }

  throw new Error('Your organization is still being provisioned. Please try again in a moment.')
}

export function useProvisioning() {
  const status = ref<ProvisioningStatus>('LOADING');
  let pollingInterval: ReturnType<typeof setInterval> | null = null;

  const checkStatus = async () => {
    try {
      // 1. Fetch Session (No refresh needed for initial check)
      const session = await fetchAuthSession();

      // Safety check: ensure tokens exist
      if (!session.tokens?.idToken?.payload) {
        // Not authenticated or session invalid
        status.value = 'ERROR';
        return;
      }

      const claims = session.tokens.idToken.payload as ClaimPayload;
      const resolvedStatus = resolveProvisioningStatus(claims)

      // 2. Logic Check
      if (resolvedStatus === 'PROVISIONING') {
        status.value = resolvedStatus
        startPolling();
      }
      else {
        if (resolvedStatus === 'ERROR') {
          // Edge Case: Logged in, but no complete org/tenant context and no provisioning flag
          console.error("Missing org/tenant claims and no provisioning status found.");
        }
        status.value = resolvedStatus
      }
    } catch (e) {
      console.error("Auth check failed:", e);
      status.value = 'ERROR';
    }
  };

  const startPolling = () => {
    if (pollingInterval) return;

    let attempts = 0;
    const maxAttempts = 10; // 30 seconds max

    pollingInterval = setInterval(async () => {
      attempts++;
      console.log(`Polling for provisioning... Attempt ${attempts}`);

      try {
        // v6: Force Refresh
        const newSession = await forceTokenRefresh();

        // Access payload safely
        const newClaims = newSession.tokens?.idToken?.payload as ClaimPayload;
        const refreshedStatus = resolveProvisioningStatus(newClaims)

        if (refreshedStatus === 'READY') {
          stopPolling();
          status.value = refreshedStatus;
        } else if (attempts >= maxAttempts) {
          stopPolling();
          status.value = 'ERROR';
        }
      } catch {
        stopPolling();
        status.value = 'ERROR';
      }
    }, 3000);
  };

  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
  };

  onMounted(() => {
    checkStatus();
  });

  onUnmounted(() => {
    stopPolling();
  });

  return { status };
}
